// tours.ts — the content of both product tours. THE file to edit when a
// feature ships. Checklist (also in .claude/rules/product-tour.md):
//
//   1. Does the feature change what a new user should learn on day one?
//      If yes, add a step (anchor via a `data-tour="..."` attribute in the
//      component, never a CSS class) and bump `version`; set the new step's
//      `addedIn` to that version so returning users see only what's new.
//   2. Did a limit, price tier or label change? Fix the copy. Plan-specific
//      numbers should read from planCapabilities.ts, not be typed here.
//   3. Walk both tours in the browser (replay from the user menu), then update
//      `reviewedOn`. tours.test.ts fails the build if a step's anchor no longer
//      exists in the source, which is the bug that orphaned the old tour.

import { FREE_WATCH_LIMIT, SCOUT_BUY_BOX_LIMIT, SCOUT_COUNTY_LIMIT, SCOUT_WATCH_LIMIT } from "../planCapabilities";
import type { UserPlan } from "../planCapabilities";
import type { TourDef } from "./types";

const isScout = (plan: UserPlan) => plan === "scout";

export const FREE_TOUR: TourDef = {
  id: "free",
  version: 1,
  reviewedOn: "2026-10-27",
  doneLabel: "Start exploring",
  steps: [
    {
      id: "welcome",
      title: "Welcome to Parcely",
      body: "North Carolina foreclosures and pre-foreclosure leads on one map. This quick tour shows where everything lives.",
      addedIn: 1,
    },
    {
      id: "county",
      anchor: "county-selector",
      view: "all",
      title: "Your county",
      body: "Free includes one county of your choice. Browse everything in it from here.",
      addedIn: 1,
    },
    {
      id: "legend",
      anchor: "map-legend",
      view: "all",
      side: "top",
      title: "Read the pins",
      body: "Red pins are auctions with a sale date, blue are pre-auction leads, gray are resolved. The legend is always one click away.",
      addedIn: 1,
    },
    {
      id: "search",
      anchor: "search",
      view: "all",
      side: "bottom",
      title: "Search and filter",
      body: "Look up an address or parcel ID, then narrow the list by status and property type.",
      addedIn: 1,
    },
    {
      id: "card",
      anchor: "property-card",
      view: "all",
      side: "right",
      title: "Open any property",
      body: "Click a card for ownership, distress signals, court records and research links. Free opens full detail on a handful of properties in your county.",
      addedIn: 1,
    },
    {
      id: "watch",
      anchor: "watch-star",
      view: "all",
      side: "bottom",
      title: "Save to your watchlist",
      body: `Star a property to keep it handy. Free holds ${FREE_WATCH_LIMIT} saves, Scout ${SCOUT_WATCH_LIMIT}, Pro is unlimited.`,
      addedIn: 1,
    },
    {
      id: "leads",
      anchor: "tab-leads",
      side: "bottom",
      title: "Seller Leads",
      body: "Scout and Pro unlock owner names, mailing addresses and equity on pre-auction leads, plus saved searches and new-match alerts.",
      addedIn: 1,
    },
    {
      id: "menu",
      anchor: "user-menu",
      side: "bottom",
      title: "Your menu",
      body: "Settings holds your email alerts and zip alert. You can also book a free 15-minute setup call with the founder, and replay this tour any time.",
      addedIn: 1,
    },
    {
      id: "feedback",
      anchor: "feedback",
      side: "left",
      title: "Tell us what's missing",
      body: "Found a bug or want a feature? This button sends it straight to the founder.",
      addedIn: 1,
    },
    {
      id: "done",
      title: "You're all set",
      body: "Click any pin on the map to start. Questions? The setup call is the fastest way to get answers.",
      addedIn: 1,
    },
  ],
};

export const PAID_TOUR: TourDef = {
  id: "paid",
  version: 1,
  reviewedOn: "2026-10-27",
  doneLabel: "Start exploring",
  steps: [
    {
      id: "welcome",
      title: "Welcome to Parcely",
      body: ({ plan }) =>
        isScout(plan)
          ? `You're on Scout: full Pro data inside the ${SCOUT_COUNTY_LIMIT} counties you chose. Here is everything you can use.`
          : "You're on Pro: every NC county, no limits. Here is everything you can use.",
      addedIn: 1,
    },
    {
      id: "county",
      anchor: "county-selector",
      view: "all",
      title: "Jump between counties",
      body: ({ plan }) =>
        isScout(plan)
          ? "Switch between your chosen counties here. Outside them you see locked previews."
          : "Every NC county is open to you. Switch between them here.",
      addedIn: 1,
    },
    {
      id: "legend",
      anchor: "map-legend",
      view: "all",
      side: "top",
      title: "Read the pins",
      body: "Red pins are auctions with a sale date, blue are pre-auction leads, gray are resolved. The legend is always one click away.",
      addedIn: 1,
    },
    {
      id: "search",
      anchor: "search",
      view: "all",
      side: "bottom",
      title: "Search and filter",
      body: "Look up an address or parcel ID, then narrow the list by status and property type.",
      addedIn: 1,
    },
    {
      id: "card",
      anchor: "property-card",
      view: "all",
      side: "right",
      title: "Everything on a property",
      body: "Open a card for ownership and equity, court records, upset-bid history and bid stats, and the deal analyzer with DealScore.",
      addedIn: 1,
    },
    {
      id: "watch",
      anchor: "watch-star",
      view: "all",
      side: "bottom",
      title: "Watch what matters",
      body: ({ plan }) =>
        `${isScout(plan) ? `Scout holds ${SCOUT_WATCH_LIMIT} saves.` : "Your watchlist is unlimited."} Watched properties feed your email alerts and Portfolio.`,
      addedIn: 1,
    },
    {
      id: "leads",
      anchor: "tab-leads",
      view: "leads",
      side: "bottom",
      title: "Seller Leads",
      body: ({ plan }) =>
        isScout(plan)
          ? "Pre-auction owners with mailing address and equity, across your chosen counties. Export the list to CSV."
          : "Pre-auction owners with mailing address and equity, statewide. Export the list or the full buyer list to CSV.",
      addedIn: 1,
    },
    {
      id: "buybox",
      anchor: "buy-box",
      view: "leads",
      side: "bottom",
      title: "Save a search",
      body: ({ plan }) =>
        `Set your buy box once and save it. ${isScout(plan) ? `Scout keeps up to ${SCOUT_BUY_BOX_LIMIT} saved searches` : "Pro keeps unlimited saved searches"}, each counting its new matches for you.`,
      addedIn: 1,
    },
    {
      id: "bell",
      anchor: "notif-bell",
      side: "bottom",
      title: "Your new-match bell",
      body: "Saved searches report new matches here since you last looked. Click a row to jump straight to those leads.",
      addedIn: 1,
    },
    {
      id: "portfolio",
      anchor: "tab-portfolio",
      side: "bottom",
      title: "Portfolio",
      body: "Aggregate stats, auction timeline and equity across everything you watch. It unlocks once you are watching a few properties.",
      addedIn: 1,
    },
    {
      id: "menu",
      anchor: "user-menu",
      side: "bottom",
      title: "Settings and alerts",
      body: "Settings has your email alerts, the auction calendar feed, your counties, map preferences and billing. You can also book a free setup call or replay this tour here.",
      addedIn: 1,
    },
    {
      id: "feedback",
      anchor: "feedback",
      side: "left",
      title: "Tell us what's missing",
      body: "Found a bug or want a feature? This button sends it straight to the founder.",
      addedIn: 1,
    },
    {
      id: "done",
      title: "You're all set",
      body: "Open a property from the map or the list to put it to work.",
      addedIn: 1,
    },
  ],
};

export function tourForPlan(plan: UserPlan): TourDef {
  return plan === "free" ? FREE_TOUR : PAID_TOUR;
}

export const ALL_TOURS: TourDef[] = [FREE_TOUR, PAID_TOUR];
